/**
 * The whole-base filters as the `/assistant` page edits them, and as a
 * question sends them (#765 Ф3). Pure: the page keeps the form, this decides
 * what reaches `assistantOrgFiltersSchema`.
 */

import {
  assistantOrgFiltersKey,
  type AssistantEntityNamesFilters,
  type AssistantOrgFilters,
} from '@aica/api-types/schemas';

/** What the controls hold: one project, one agent, local calendar days. */
export interface OrgFilterForm {
  /** `YYYY-MM-DD`, the organization's viewer's local day. */
  from?: string;
  to?: string;
  projectId?: string;
  agentId?: string;
  type?: 'call' | 'chat';
  callCategory?: string;
  /** Picked from the suggestions, as stored — never typed text. */
  entityName?: string;
  /**
   * The filters of the shown thread's last question, verbatim — sent as they
   * are until the user edits a control. The controls hold one project, one
   * agent and whole days; a thread asked through the API with more (several
   * projects, a period from noon) would otherwise be continued under filters
   * narrower or wider than its history was asked under (Codex P2, #1312).
   */
  exact?: AssistantOrgFilters;
}

/**
 * Local midnight of a `YYYY-MM-DD` shifted by `days`, or `undefined` for a
 * malformed day. By the calendar, not by 24-hour steps: a day that changes to
 * or from daylight saving time is 23 or 25 hours long.
 */
function localMidnight(day: string, days = 0): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return undefined;
  const [year, month, date] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  // `Date` rolls an impossible day over instead of refusing it — «2026-13-45»
  // would become a day in 2027 and filter the question by it.
  const base = new Date(year, month, date);
  if (
    base.getFullYear() !== year ||
    base.getMonth() !== month ||
    base.getDate() !== date
  ) {
    return undefined;
  }
  return new Date(year, month, date + days);
}

/**
 * The request's filters, or `undefined` when nothing narrows the question —
 * an empty object would travel as «filtered by nothing» and be stored on the
 * turn as such.
 *
 * The period covers whole local days: `to` is the last instant of its day,
 * or a question «for September 30» would miss everything said on the 30th.
 */
export function toOrgFilters(form: OrgFilterForm): AssistantOrgFilters | undefined {
  if (form.exact) return Object.keys(form.exact).length > 0 ? form.exact : undefined;
  const filters: AssistantOrgFilters = {};
  const from = form.from ? localMidnight(form.from) : undefined;
  if (from) filters.dateFrom = from.toISOString();
  const nextDay = form.to ? localMidnight(form.to, 1) : undefined;
  if (nextDay) filters.dateTo = new Date(nextDay.getTime() - 1).toISOString();
  if (form.projectId) filters.projectIds = [form.projectId];
  if (form.agentId) filters.agentIds = [form.agentId];
  if (form.type) filters.type = form.type;
  if (form.callCategory) filters.callCategory = form.callCategory;
  if (form.entityName) filters.entityName = form.entityName;
  return Object.keys(filters).length > 0 ? filters : undefined;
}

/**
 * The question's filters other than the name — what the customer suggestions
 * are read under, so a suggested name has a record under them (#1361).
 */
export function otherOrgFilters(
  form: OrgFilterForm
): AssistantEntityNamesFilters | undefined {
  const { entityName: _chosen, ...others } = toOrgFilters(form) ?? {};
  return Object.keys(others).length > 0 ? others : undefined;
}

function localDay(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * The form a stored turn's filters came from — to continue a thread under
 * the filters it was asked with. The controls show what they can hold (the
 * first of several projects or agents, the days of the period); `exact`
 * keeps the filters themselves until a control is edited.
 */
export function fromOrgFilters(filters: AssistantOrgFilters | null): OrgFilterForm {
  if (!filters) return {};
  return {
    exact: filters,
    from: localDay(filters.dateFrom),
    to: localDay(filters.dateTo),
    projectId: filters.projectIds?.[0],
    agentId: filters.agentIds?.[0],
    type: filters.type,
    callCategory: filters.callCategory,
    entityName: filters.entityName,
  };
}

/** Which request field each control sets. */
const CONTROL_FIELDS = {
  from: 'dateFrom',
  to: 'dateTo',
  projectId: 'projectIds',
  agentId: 'agentIds',
  type: 'type',
  callCategory: 'callCategory',
  entityName: 'entityName',
} as const satisfies Record<
  Exclude<keyof OrgFilterForm, 'exact'>,
  keyof AssistantOrgFilters
>;

/**
 * The form after the user edits controls. With the verbatim filters of a
 * thread in the form (`exact`), an edit replaces only the fields of the
 * controls it touched: editing the type of a thread asked about two projects
 * keeps both projects rather than the one the control shows (Codex P2,
 * #1312). Without them the controls are the whole story.
 */
export function editOrgForm(
  form: OrgFilterForm,
  edit: Partial<Omit<OrgFilterForm, 'exact'>>
): OrgFilterForm {
  // A customer is picked under the other filters; changing one of them drops
  // the pick in the same edit — no request, so nothing to race a question
  // sent right after or to fail unseen (Codex P2, #1374). The field keeps the
  // name, and its suggestions — under the new filters — offer it again if
  // they still can.
  const patch =
    form.entityName !== undefined && !('entityName' in edit)
      ? { ...edit, entityName: undefined }
      : edit;
  const next: OrgFilterForm = { ...form, ...patch };
  if (!form.exact) return next;
  const fromControls = toOrgFilters({ ...next, exact: undefined }) ?? {};
  const exact: Record<string, unknown> = { ...form.exact };
  for (const control of Object.keys(patch) as Array<keyof typeof CONTROL_FIELDS>) {
    const field = CONTROL_FIELDS[control];
    if (fromControls[field] === undefined) delete exact[field];
    else exact[field] = fromControls[field];
  }
  return { ...next, exact: exact as AssistantOrgFilters };
}

/** One canonical text per set of filters: equal filters, equal key. */
export function orgFiltersKey(filters: AssistantOrgFilters | null | undefined): string {
  return assistantOrgFiltersKey(filters);
}

/**
 * Whether two questions were asked under the same filters. Instants compare
 * as instants — the server stores the offset it was sent, the page sends `Z`.
 */
export function sameOrgFilters(
  a: AssistantOrgFilters | null | undefined,
  b: AssistantOrgFilters | null | undefined
): boolean {
  return orgFiltersKey(a) === orgFiltersKey(b);
}
